/**
 * Order-entry latency probe — the M3b investigation instrument.
 *
 * Traders report ~1s from hotkey press to hearing the placed tone, and a
 * belief that clicks land faster than hotkeys. Both paths share
 * `oneClickStore.submitIntent`, so any real gap is upstream (fence
 * resolution, in-flight refusals, pacing drops) or perceptual (the tone
 * plays only after the full relay→venue round trip). This probe stamps the
 * shared lane so the two input paths can be compared on identical marks:
 *
 *   input     — keydown (hotkey) / pointer intercept (mouse)
 *   submit    — submitIntent entered (guard about to run)
 *   response  — placeOrder settled (the tone plays within ms of this)
 *
 * One attempt at a time (the hotkey _inFlight lock and pacing already
 * serialize order entry); a new start while one is open finalizes the old
 * attempt as-is. Ring buffer of the last 50 samples; dump from any console
 * via `window.__orderLatency()`. Zero overhead beyond a few performance.now
 * calls — safe to leave on in production builds.
 *
 * Clock: performance.now() (speed-demon 260904, T-A3) — monotonic, sub-ms,
 * immune to wall-clock steps mid-attempt. Marks stay "ms since start", so
 * samples recorded before/after the switch remain comparable.
 */
import { relayVenueSplit } from './serverTiming';

export interface OrderLatencySample {
  input: 'mouse' | 'hotkey-a' | 'hotkey-d';
  label: string;
  /** performance.now() reading at the initiating input event (monotonic ms,
   *  NOT epoch — compare marks, not startedMs, across page loads). */
  startedMs: number;
  /** Mark name → ms since start. */
  marks: Record<string, number>;
  /** Relay Server-Timing stages (cumulative ms since relay receipt), attached
   *  by postOrderRaw when the response carried the header. */
  serverTiming?: Record<string, number>;
}

const RING_MAX = 50;
const ring: OrderLatencySample[] = [];
let current: OrderLatencySample | null = null;

export function probeStart(input: OrderLatencySample['input'], label: string): void {
  if (current) finalize();
  current = { input, label, startedMs: performance.now(), marks: {} };
}

export function probeMark(name: string): void {
  if (!current) return;
  current.marks[name] = performance.now() - current.startedMs;
}

/** Attach the relay's parsed Server-Timing stages to the in-flight sample.
 *  No-op when no attempt is open (e.g. a ticket order placed without a probe). */
export function probeAttachServerTiming(stages: Record<string, number>): void {
  if (!current) return;
  current.serverTiming = stages;
}

function finalize(): void {
  if (!current) return;
  ring.push(current);
  if (ring.length > RING_MAX) ring.shift();
  current = null;
}

/** Finalize the open attempt as-is (refusal paths — marks say how far it got). */
export function probeFinish(): void {
  finalize();
}

export function probeEnd(): void {
  probeMark('response');
  finalize();
}

export function orderLatencySamples(): OrderLatencySample[] {
  return [...ring];
}

/** The newest completed sample — the live view's perf HUD line. */
export function lastOrderLatency(): OrderLatencySample | null {
  return ring.length ? ring[ring.length - 1] : null;
}

declare global {
  interface Window {
    __orderLatency?: () => OrderLatencySample[];
  }
}
if (typeof window !== 'undefined') {
  window.__orderLatency = () => {
    const rows = orderLatencySamples();
    // eslint-disable-next-line no-console -- the probe's whole job
    console.table(
      rows.map((r) => {
        const submit = r.marks.submit ?? null;
        const request = r.marks.request ?? null;
        const response = r.marks.response ?? null;
        const { relayMs, venueMs } = relayVenueSplit(r.serverTiming ?? null);
        return {
          input: r.input,
          label: r.label,
          submit,
          request,
          response,
          /** Client-side latency between entering submitIntent and the wire. */
          preSend: submit != null && request != null ? request - submit : null,
          /** Round trip minus relay-internal time (network + venue when the
           *  relay stamped Server-Timing; null without the header). */
          net:
            response != null && request != null && relayMs != null
              ? response - request - relayMs
              : null,
          relay: relayMs,
          venue: venueMs,
        };
      }),
    );
    return rows;
  };
}
